/**
 * Valores de la guía de diseño (claro, oscuro y alto contraste).
 *
 * Fuente única: `.docs/styleGuideReferemce/design-tokens.md`, leído de
 * `Guía de diseño — claro, oscuro y alto contraste@2x.png`. Los temas de Fluent v9 / v8 y las
 * variables CSS `--ichtys-*` se arman a partir de este archivo: si la guía cambia, se cambia acá.
 * Los componentes no llevan hex: usan tokens de Fluent o las variables CSS.
 */

export type ThemeMode = 'light' | 'dark' | 'high-contrast';

export const THEME_MODES: ThemeMode[] = ['light', 'dark', 'high-contrast'];

/** Tema por defecto para todo usuario: el claro de la guía (no se sigue la preferencia del SO). */
export const DEFAULT_THEME_MODE: ThemeMode = 'light';

export const isThemeMode = (value: unknown): value is ThemeMode =>
  typeof value === 'string' && (THEME_MODES as string[]).includes(value);

export interface GuidePalette {
  primary: string;
  /** Primario oscuro (sólo claro); en los otros modos se repite el primario. */
  primaryDark: string;
  /** Acento / links. */
  accent: string;
  /** Color de marca del degradé del encabezado y la barra lateral. */
  brandGradient: string;
  background: string;
  surface: string;
  border: string;
  text: string;
  textSecondary: string;
  success: string;
  warning: string;
  confirmed: string;
  /** Crítico (claro) / error (oscuro y alto contraste). */
  critical: string;
  /** Error de validación de campos. */
  error: string;
  /** Texto sobre el relleno primario (botón primario). */
  onPrimary: string;
  /** Fondo del tooltip. */
  tooltipBackground: string;
  tooltipText: string;
  /** Fondo de controles deshabilitados / sólo lectura. */
  disabledBackground: string;
  disabledText: string;
}

export const guidePalettes: Record<ThemeMode, GuidePalette> = {
  light: {
    primary: '#1976d2',
    primaryDark: '#0d47a1',
    accent: '#1976d2',
    brandGradient: '#1e73be',
    background: '#f5f5f5',
    surface: '#ffffff',
    border: '#e0e0e0',
    text: '#212529',
    textSecondary: '#5b6675',
    success: '#2e7d32',
    warning: '#e8913a',
    confirmed: '#5e35b1',
    critical: '#a14f05',
    error: '#c62828',
    onPrimary: '#ffffff',
    tooltipBackground: '#212529',
    tooltipText: '#ffffff',
    disabledBackground: '#eef1f5',
    disabledText: '#5b6675',
  },
  dark: {
    primary: '#1976d2',
    primaryDark: '#1976d2',
    accent: '#64b5f6',
    brandGradient: '#16508a',
    background: '#121417',
    surface: '#1c2025',
    border: '#2e343c',
    text: '#e8eaed',
    textSecondary: '#a0a8b3',
    success: '#81c995',
    warning: '#ffb74d',
    confirmed: '#b39ddb',
    critical: '#ff8a65',
    error: '#ff8a65',
    onPrimary: '#ffffff',
    tooltipBackground: '#e8eaed',
    tooltipText: '#121417',
    disabledBackground: '#2e343c',
    disabledText: '#a0a8b3',
  },
  'high-contrast': {
    primary: '#ffe600',
    primaryDark: '#ffe600',
    accent: '#ffe600',
    brandGradient: '#000000',
    background: '#000000',
    surface: '#000000',
    border: '#ffffff',
    text: '#ffffff',
    textSecondary: '#e6e6e6',
    success: '#5cff8d',
    warning: '#ffb13b',
    confirmed: '#e0b8ff',
    critical: '#ff7a7a',
    error: '#ff7a7a',
    onPrimary: '#000000',
    tooltipBackground: '#000000',
    tooltipText: '#ffffff',
    disabledBackground: '#000000',
    disabledText: '#a6a6a6',
  },
};

/** Tonos de las etiquetas de estado (pastilla con borde, fondo tenue del mismo tono, texto en mayúsculas). */
export type StatusTone = 'success' | 'warning' | 'info' | 'brand' | 'confirmed' | 'danger' | 'neutral';

export const STATUS_TONES: StatusTone[] = ['success', 'warning', 'info', 'brand', 'confirmed', 'danger', 'neutral'];

interface PillColors {
  fg: string;
  bg: string;
  border: string;
}

/**
 * Pastillas de estado por modo, según "Etiquetas de estado" de la guía:
 * ACTIVO / PAGADO → success, SIN REVISAR → warning (texto marrón #a14f05 en claro),
 * REVISADO → info (azul tenue, texto azul oscuro), FINALIZADO → brand (contorno azul),
 * CONFIRMADO → confirmed (violeta), INACTIVO → neutral (gris), error → danger.
 * En alto contraste el fondo es negro y el borde toma el color del texto.
 */
export const statusPillColors: Record<ThemeMode, Record<StatusTone, PillColors>> = {
  light: {
    success: { fg: '#2e7d32', bg: '#e8f5e9', border: '#a5d6a7' },
    warning: { fg: '#a14f05', bg: '#fff4e0', border: '#f6c27e' },
    info: { fg: '#0d47a1', bg: '#e3f0fd', border: '#90caf9' },
    brand: { fg: '#1976d2', bg: '#ffffff', border: '#1976d2' },
    confirmed: { fg: '#5e35b1', bg: '#ede7f6', border: '#b39ddb' },
    danger: { fg: '#c62828', bg: '#fdecea', border: '#ef9a9a' },
    neutral: { fg: '#5b6675', bg: '#eef1f5', border: '#c3cad3' },
  },
  dark: {
    success: { fg: '#81c995', bg: 'rgba(129, 201, 149, 0.14)', border: 'rgba(129, 201, 149, 0.55)' },
    warning: { fg: '#ffb74d', bg: 'rgba(255, 183, 77, 0.14)', border: 'rgba(255, 183, 77, 0.55)' },
    info: { fg: '#64b5f6', bg: 'rgba(100, 181, 246, 0.14)', border: 'rgba(100, 181, 246, 0.55)' },
    brand: { fg: '#64b5f6', bg: 'transparent', border: '#64b5f6' },
    confirmed: { fg: '#b39ddb', bg: 'rgba(179, 157, 219, 0.14)', border: 'rgba(179, 157, 219, 0.55)' },
    danger: { fg: '#ff8a65', bg: 'rgba(255, 138, 101, 0.14)', border: 'rgba(255, 138, 101, 0.55)' },
    neutral: { fg: '#a0a8b3', bg: 'rgba(160, 168, 179, 0.12)', border: '#2e343c' },
  },
  'high-contrast': {
    success: { fg: '#5cff8d', bg: '#000000', border: '#5cff8d' },
    warning: { fg: '#ffb13b', bg: '#000000', border: '#ffb13b' },
    info: { fg: '#80d8ff', bg: '#000000', border: '#80d8ff' },
    brand: { fg: '#80d8ff', bg: '#000000', border: '#80d8ff' },
    confirmed: { fg: '#e0b8ff', bg: '#000000', border: '#e0b8ff' },
    danger: { fg: '#ff7a7a', bg: '#000000', border: '#ff7a7a' },
    neutral: { fg: '#ffffff', bg: '#000000', border: '#e6e6e6' },
  },
};

/** Fondos tenues de los banners y de las filas (seleccionada / pendiente / hover). */
const tints: Record<ThemeMode, Record<'info' | 'warning' | 'danger' | 'success' | 'selectedRow' | 'pendingRow' | 'hoverRow', string>> = {
  light: {
    info: '#e8f1fc',
    warning: '#fff4e0',
    danger: '#fdecea',
    success: '#e8f5e9',
    selectedRow: '#e8f1fc',
    pendingRow: '#fffaf2',
    hoverRow: '#f5f7fa',
  },
  dark: {
    info: 'rgba(100, 181, 246, 0.12)',
    warning: 'rgba(255, 183, 77, 0.12)',
    danger: 'rgba(255, 138, 101, 0.12)',
    success: 'rgba(129, 201, 149, 0.12)',
    selectedRow: 'rgba(100, 181, 246, 0.14)',
    pendingRow: 'rgba(255, 183, 77, 0.08)',
    hoverRow: 'rgba(255, 255, 255, 0.04)',
  },
  'high-contrast': {
    info: '#000000',
    warning: '#000000',
    danger: '#000000',
    success: '#000000',
    selectedRow: '#000000',
    pendingRow: '#000000',
    hoverRow: '#000000',
  },
};

/**
 * Variables CSS semánticas para lo que los tokens de Fluent no cubren. Se aplican en `<html>`
 * (para que lleguen también a los portales) junto con `data-theme`.
 */
/** '#1e73be' → '30, 115, 190' (para usar en rgba(var(--x), alfa)). */
export const hexToRgbTriplet = (hex: string): string => {
  const clean = hex.replace('#', '');
  return [0, 2, 4].map((i) => parseInt(clean.slice(i, i + 2), 16)).join(', ');
};

export const themeCssVars = (mode: ThemeMode): Record<string, string> => {
  const p = guidePalettes[mode];
  const tint = tints[mode];
  const vars: Record<string, string> = {
    '--ichtys-primary': p.primary,
    '--ichtys-primary-dark': p.primaryDark,
    '--ichtys-on-primary': p.onPrimary,
    '--ichtys-accent': p.accent,
    '--ichtys-link': p.accent,
    '--ichtys-focus': p.primary,
    '--ichtys-brand-gradient': p.brandGradient,
    '--ichtys-brand-gradient-rgb': hexToRgbTriplet(p.brandGradient),
    '--ichtys-bg': p.background,
    '--ichtys-surface': p.surface,
    '--ichtys-border': p.border,
    '--ichtys-text': p.text,
    '--ichtys-text-secondary': p.textSecondary,
    '--ichtys-success': p.success,
    '--ichtys-warning': p.warning,
    '--ichtys-confirmed': p.confirmed,
    '--ichtys-critical': p.critical,
    '--ichtys-error': p.error,
    '--ichtys-tooltip-bg': p.tooltipBackground,
    '--ichtys-tooltip-text': p.tooltipText,
    '--ichtys-disabled-bg': p.disabledBackground,
    '--ichtys-disabled-text': p.disabledText,
    '--ichtys-banner-info-bar': mode === 'light' ? p.primaryDark : statusPillColors[mode].info.fg,
    '--ichtys-banner-info-bg': tint.info,
    '--ichtys-banner-warning-bar': mode === 'light' ? p.critical : p.warning,
    '--ichtys-banner-warning-bg': tint.warning,
    '--ichtys-banner-error-bar': p.error,
    '--ichtys-banner-error-bg': tint.danger,
    '--ichtys-banner-success-bar': p.success,
    '--ichtys-banner-success-bg': tint.success,
    '--ichtys-row-selected-bg': tint.selectedRow,
    '--ichtys-row-selected-bar': p.primary,
    '--ichtys-row-pending-bg': tint.pendingRow,
    '--ichtys-row-hover-bg': tint.hoverRow,
    '--ichtys-pill-border-width': mode === 'high-contrast' ? '2px' : '1px',
    // Drawer (panel lateral): borde izquierdo y sombra; en alto contraste borde blanco de 2 px sin sombra.
    '--ichtys-drawer-border-width': mode === 'high-contrast' ? '2px' : '1px',
    '--ichtys-drawer-shadow':
      mode === 'high-contrast' ? 'none' : mode === 'dark' ? '-8px 0 24px rgba(0, 0, 0, 0.55)' : '-8px 0 24px rgba(33, 37, 41, 0.16)',
    // Foco de campos: borde primario + halo tenue (alto contraste: halo sólido amarillo, sin transparencia).
    '--ichtys-focus-halo': mode === 'high-contrast' ? p.primary : `rgba(${hexToRgbTriplet(p.primary)}, 0.25)`,
  };
  STATUS_TONES.forEach((tone) => {
    const c = statusPillColors[mode][tone];
    vars[`--ichtys-pill-${tone}-fg`] = c.fg;
    vars[`--ichtys-pill-${tone}-bg`] = c.bg;
    vars[`--ichtys-pill-${tone}-border`] = c.border;
  });
  return vars;
};

export const GUIDE_FONT_FAMILY = "Lato, 'Segoe UI', Arial, sans-serif";
